import React, { useEffect, useState, useMemo, useRef } from 'react';
import { collection, doc, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { CreativeSession, CreativeStudentWork, User } from '../../../types';
import { useAuth } from '../../auth/contexts/AuthContext';
import { StudentAuthService } from '../../../shared/storage/StudentAuthService';
import { serverTimestamp } from 'firebase/firestore';

interface OGCreatorsGameProps {
  onBackToMenu: () => void;
}

const MAX_CHARACTERS = 1000; // Roughly ~3 short paragraphs

const OGCreatorsGame: React.FC<OGCreatorsGameProps> = ({ onBackToMenu }) => {
  const { currentUser, userData } = useAuth();
  const [student, setStudent] = useState<User | null>(null);
  const [squadId, setSquadId] = useState<string | null>(null);
  const [session, setSession] = useState<CreativeSession | null>(null);
  const [text, setText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializedFromServerRef = useRef(false);

  // Resolve student + squad context using existing identity + StudentAuthService
  useEffect(() => {
    const loadStudentContext = async () => {
      try {
        if (userData && userData.role === 'student') {
          setStudent(userData as User);
          const localInfo = StudentAuthService.getLocalStudentInfo();
          const inferredSquadId = userData.currentSquadId || localInfo?.squadId || null;
          setSquadId(inferredSquadId);
          return;
        }

        // Anonymous or non-student: try StudentAuthService
        const studentData = await StudentAuthService.getCurrentStudentData();
        if (studentData) {
          setStudent(studentData as User);
          const localInfo = StudentAuthService.getLocalStudentInfo();
          setSquadId(localInfo?.squadId || studentData.currentSquadId || null);
          return;
        }

        setError('Unable to determine student identity. Please log in again or ask your teacher for help.');
      } catch (e) {
        console.error('Failed to resolve student context for OG Creators', e);
        setError('Something went wrong while setting up OG Creators.');
      }
    };

    loadStudentContext();
  }, [currentUser, userData]);

  // Subscribe to active creativeSession for this squad
  useEffect(() => {
    if (!squadId) return;

    const sessionsQuery = query(
      collection(db, 'creativeSessions'),
      where('squadId', '==', squadId),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(sessionsQuery, (snap) => {
      if (snap.empty) {
        setSession(null);
        return;
      }
      const docSnap = snap.docs[0];
      const data = docSnap.data() as any;
      setSession({
        id: docSnap.id,
        squadId: data.squadId,
        teacherId: data.teacherId,
        type: data.type || 'prompting',
        startTime: data.startTime,
        endTime: data.endTime,
        status: data.status,
        missionText: data.missionText,
        constraints: data.constraints,
      });
    }, (err) => {
      console.error('Failed to load creative session for OG Creators', err);
      setError('Failed to connect to the creative session.');
    });

    return () => unsubscribe();
  }, [squadId]);

  // Subscribe to this student's work within the current session
  useEffect(() => {
    if (!session || !student) return;

    // Reset initialization when session or student changes
    initializedFromServerRef.current = false;

    const workRef = doc(db, 'creativeSessions', session.id, 'studentWork', student.id);
    const unsubscribe = onSnapshot(workRef, (snap) => {
      if (!snap.exists()) {
        if (!initializedFromServerRef.current) {
          setText('');
          setReady(false);
          initializedFromServerRef.current = true;
        }
        return;
      }
      const data = snap.data() as any;
      const currentWork: CreativeStudentWork = {
        studentId: data.studentId,
        squadId: data.squadId,
        sessionId: session.id,
        promptText: data.promptText || '',
        status: data.status || 'draft',
        teacherFeedback: data.teacherFeedback,
        updatedAt: data.updatedAt,
      };
      // Only initialize from Firestore once so we don't overwrite the student's
      // in-progress typing with slightly older server data.
      if (!initializedFromServerRef.current) {
        setText(currentWork.promptText);
        setReady(currentWork.status === 'submitted' || currentWork.status === 'ready');
        initializedFromServerRef.current = true;
      } else {
        // Still keep local ready state in sync with server, but leave text alone.
        setReady(currentWork.status === 'submitted' || currentWork.status === 'ready');
      }
    });

    return () => unsubscribe();
  }, [session, student]);

  // Debounced save when text changes
  useEffect(() => {
    if (!session || !student) return;
    if (!session.id || !student.id) return;

    setIsSaving(true);
    const handler = setTimeout(async () => {
      try {
        const workRef = doc(db, 'creativeSessions', session.id, 'studentWork', student.id);
        const payload = {
          studentId: student.id,
          squadId: squadId,
          sessionId: session.id,
          promptText: text,
          status: ready ? 'submitted' : 'draft',
          updatedAt: serverTimestamp() as any,
        };
        await setDoc(workRef, payload, { merge: true });
      } catch (e) {
        console.error('Failed to save creative work for OG Creators', e);
        setError('We had trouble saving. Your teacher may not see the latest text.');
      } finally {
        setIsSaving(false);
      }
    }, 400); // ~real-time without spamming writes

    return () => clearTimeout(handler);
  }, [text, ready, session, squadId, student]);

  const remainingChars = useMemo(() => {
    return Math.max(0, MAX_CHARACTERS - text.length);
  }, [text.length]);

  const disabled = !session || !student || !!error;

  return (
    <div
      style={{
        padding: '24px 16px',
        maxWidth: 900,
        margin: '0 auto',
        fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif',
      }}
    >
      <button
        onClick={onBackToMenu}
        style={{
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: '#3b82f6',
          cursor: 'pointer',
          fontWeight: 700,
        }}
      >
        ← Back to Games
      </button>

      <h1 style={{ fontSize: '2.25rem', marginBottom: 8 }}>OG Creators (Beta)</h1>
      <p style={{ marginBottom: 16, color: '#4b5563' }}>
        Type your most creative idea! Your teacher can see all the words you type.
      </p>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 12,
            background: '#fee2e2',
            color: '#b91c1c',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {!session && !error && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 12,
            background: '#e0f2fe',
            color: '#1e3a8a',
            fontSize: 14,
          }}
        >
          Waiting for your teacher to start an OG Create activity for this class…
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          background: '#ffffff',
          borderRadius: 18,
          padding: 16,
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
          border: '2px solid #e0f2fe',
        }}
      >
        <textarea
          value={text}
          onChange={(e) => {
            const next = e.target.value;
            if (next.length <= MAX_CHARACTERS) {
              setText(next);
            }
          }}
          disabled={disabled || (session && session.status !== 'active')}
          placeholder={
            session
              ? 'Start typing your prompt here…'
              : 'Waiting for your teacher to start the activity…'
          }
          style={{
            width: '100%',
            minHeight: 220,
            borderRadius: 12,
            border: 'none',
            resize: 'vertical',
            padding: 12,
            fontSize: 16,
            lineHeight: 1.5,
            fontFamily: 'inherit',
            outline: 'none',
            background: '#f9fafb',
          }}
        />

        <div
          style={{
            marginTop: 8,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 13,
            color: '#6b7280',
          }}
        >
          <span>
            {remainingChars} characters left (about 3 paragraphs)
          </span>
          <span>{isSaving ? 'Saving…' : 'Saved'}</span>
        </div>

        <div
          style={{
            marginTop: 16,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!ready ? (
            <button
              onClick={() => setReady(true)}
              disabled={disabled}
              style={{
                padding: '0.9rem 3.1rem',
                borderRadius: 999,
                border: 'none',
                background: '#22c55e',
                color: '#fff',
                cursor: disabled ? 'default' : 'pointer',
                fontWeight: 900,
                fontSize: '1.2rem',
                boxShadow: '0 8px 22px rgba(34,197,94,0.55)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                transition: 'background-color 150ms ease-out, transform 150ms ease-out',
              }}
            >
              <span style={{ fontSize: 24 }}>✅</span>
              I am done!
            </button>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '0.85rem 2.4rem',
                borderRadius: 999,
                background: '#fef3c7',
                color: '#92400e',
                fontWeight: 900,
                fontSize: '1.05rem',
                boxShadow: '0 6px 18px rgba(251, 191, 36, 0.45)',
              }}
            >
              <span style={{ fontSize: 28 }}>✋</span>
              <span>Raise your hand!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OGCreatorsGame;


